import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Edit,
  Search,
  Filter,
  File,
  Loader2,
  ExternalLink,
  FolderOpen,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const AcademyDocuments = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Upload dialog state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general',
    file: null
  });
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const categories = [
    { value: 'general', label: 'General' },
    { value: 'policy', label: 'Policy' },
    { value: 'handbook', label: 'Handbook' },
    { value: 'forms', label: 'Forms' },
    { value: 'safety', label: 'Safety' },
    { value: 'other', label: 'Other' }
  ];
  
  useEffect(() => {
    fetchDocuments();
  }, []);
  
  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents/academy');
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpload = async () => {
    if (!uploadForm.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    if (!uploadForm.file) {
      toast.error('Please select a PDF file to upload');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description || '');
      formData.append('category', uploadForm.category);
      formData.append('file', uploadForm.file);
      
      await api.post('/documents/academy', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully');
      setUploadDialog(false);
      setUploadForm({ title: '', description: '', category: 'general', file: null });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };
  
  const handleUpdate = async () => {
    if (!editingDoc) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', editingDoc.title);
      formData.append('description', editingDoc.description || '');
      formData.append('category', editingDoc.category);
      if (editingDoc.newFile) {
        formData.append('file', editingDoc.newFile);
      }
      
      await api.put(`/documents/academy/${editingDoc.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document updated successfully');
      setEditDialog(false);
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setUploading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!deletingDoc) return;
    
    setDeleting(true);
    try {
      await api.delete(`/documents/academy/${deletingDoc.id}`);
      toast.success('Document deleted successfully');
      setDeleteDialog(false);
      setDeletingDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getCategoryBadgeColor = (category) => {
    const colors = {
      general: 'bg-slate-100 text-slate-800',
      policy: 'bg-blue-100 text-blue-800',
      handbook: 'bg-purple-100 text-purple-800',
      forms: 'bg-green-100 text-green-800',
      safety: 'bg-red-100 text-red-800',
      other: 'bg-amber-100 text-amber-800'
    };
    return colors[category] || colors.general;
  };
  
  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (doc.description && doc.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="academy-documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FolderOpen className="h-6 w-6 text-blue-600" />
              Academy Documents
            </h1>
            <p className="text-slate-600 mt-1">
              Access important academy policies, handbooks, and forms
            </p>
          </div>
          
          {isAdmin && (
            <Button 
              onClick={() => setUploadDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="upload-document-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          )}
        </div>
        
        {/* Search and Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        {/* Documents List */}
        {filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No documents found</h3>
              <p className="text-slate-500">
                {documents.length === 0 
                  ? 'No documents have been uploaded yet.'
                  : 'No documents match your search criteria.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`document-card-${doc.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <File className="h-8 w-8 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate" title={doc.title}>
                        {doc.title}
                      </h3>
                      <Badge className={`mt-1 ${getCategoryBadgeColor(doc.category)}`}>
                        {categories.find(c => c.value === doc.category)?.label || doc.category}
                      </Badge>
                    </div>
                  </div>
                  
                  {doc.description && (
                    <p className="text-sm text-slate-500 mt-3 line-clamp-2">
                      {doc.description}
                    </p>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                  
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={async () => {
                        try {
                          const response = await api.get(`/documents/academy/${doc.id}/download`, {
                            responseType: 'blob'
                          });
                          // Create blob with correct PDF mime type
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const blobUrl = URL.createObjectURL(blob);
                          
                          // Create a new window and write the PDF embed directly
                          const newWindow = window.open('', '_blank');
                          if (newWindow) {
                            newWindow.document.write(`
                              <!DOCTYPE html>
                              <html>
                              <head>
                                <title>${doc.title || 'Document'}</title>
                                <style>
                                  body { margin: 0; padding: 0; }
                                  embed { width: 100%; height: 100vh; }
                                </style>
                              </head>
                              <body>
                                <embed src="${blobUrl}" type="application/pdf" width="100%" height="100%">
                              </body>
                              </html>
                            `);
                            newWindow.document.close();
                          } else {
                            // Popup was blocked, trigger download instead
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = doc.file_name || 'document.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            toast.info('Opening document as download (pop-ups may be blocked)');
                          }
                          
                          // Clean up blob URL after a delay
                          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                        } catch (error) {
                          console.error('View error:', error);
                          toast.error('Failed to open document');
                        }
                      }}
                      data-testid={`view-document-${doc.id}`}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={async () => {
                        try {
                          const response = await api.get(`/documents/academy/${doc.id}/download`, {
                            responseType: 'blob'
                          });
                          // Create blob with correct PDF mime type
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const blobUrl = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = doc.file_name || 'document.pdf';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(blobUrl);
                        } catch (error) {
                          toast.error('Failed to download document');
                        }
                      }}
                      data-testid={`download-document-${doc.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                    {isAdmin && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingDoc(doc);
                            setEditDialog(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => {
                            setDeletingDoc(doc);
                            setDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Upload Dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Upload Document
              </DialogTitle>
              <DialogDescription>
                Upload a PDF document for all academy members to access.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="title">Document Title *</Label>
                <Input
                  id="title"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  placeholder="e.g., Academy Handbook 2026"
                />
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={uploadForm.category} 
                  onValueChange={(val) => setUploadForm({...uploadForm, category: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Brief description of this document..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="file">PDF File *</Label>
                <div className="mt-1">
                  <Input
                    id="file"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                    className="file:mr-4 file:py-1 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-slate-500 mt-1">Only PDF files allowed. Max size: 10MB</p>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialog(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700">
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Document
              </DialogTitle>
            </DialogHeader>
            
            {editingDoc && (
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit-title">Document Title</Label>
                  <Input
                    id="edit-title"
                    value={editingDoc.title}
                    onChange={(e) => setEditingDoc({...editingDoc, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-category">Category</Label>
                  <Select 
                    value={editingDoc.category} 
                    onValueChange={(val) => setEditingDoc({...editingDoc, category: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingDoc.description || ''}
                    onChange={(e) => setEditingDoc({...editingDoc, description: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-file">Replace PDF (optional)</Label>
                  <Input
                    id="edit-file"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => setEditingDoc({...editingDoc, newFile: e.target.files[0]})}
                    className="file:mr-4 file:py-1 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700"
                  />
                  <p className="text-xs text-slate-500 mt-1">Current file: {editingDoc.file_name}</p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={uploading} className="bg-blue-600 hover:bg-blue-700">
                {uploading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                Delete Document
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-slate-600">
                Are you sure you want to delete "<strong>{deletingDoc?.title}</strong>"? 
                This action cannot be undone.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AcademyDocuments;
